function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
import logger from "../../logger";
import isEqual, { getType } from "../isEqual";
export var STATUS_FETCHED = "fetched";
export var STATUS_FETCHING = "fetching";
export var STATUS_NEW = "new";
export var STATUS_ERRORED = "errored";
export var STATUS_REMOVED = "removed";
function compileRegex(str) {
    switch(getType(str)){
        case "string":
            {
                if (str[0] !== "/") {
                    return null;
                }
                var i = str.lastIndexOf("/");
                if (i <= 0) {
                    return null;
                }
                try {
                    return new RegExp(str.slice(1, i), str.slice(i + 1));
                } catch (error) {
                    return null;
                }
            }
        case "regexp":
            return str;
        default:
            return null;
    }
}
/**
 * Give a specificity score to a cache entry
 * @param {object} entry
 * @returns {int}
 */ function score(param) {
    var incoming = param.config, state = param.state;
    if (state.specificity >= 0) {
        return state.specificity;
    }
    var specificity = 0; // Name is not counted as a specificity score
    // If the entry has a path, it's a more specific match
    if (incoming.path || incoming.pathRegex) {
        specificity += 1;
    }
    // If the entry has parameters, it's a more specific match
    if (incoming.parameters && Object.keys(incoming.parameters).length) {
        // calculate the specificity of the parameters
        specificity += Object.keys(incoming.parameters).length;
    }
    // If the entry has profile parameters, it's a more specific match
    if (incoming.profileParameters && Object.keys(incoming.profileParameters).length) {
        // calculate the specificity of the profile parameters
        specificity += Object.keys(incoming.profileParameters).length;
    }
    // eslint-disable-next-line no-param-reassign
    state.specificity = specificity;
    return specificity;
}
function create(entry) {
    var _entry_config, _entry, _entry_config1, _entry1, _entry_config2, _entry2, _entry_config3, _entry3, _entry_config4, _entry4, _entry_config5, _entry5, _entry_config6, _entry6, _entry_state, _entry7, _entry_state1, _entry8, _entry_state2, _entry9, _entry_state3, _entry10, _entry_state4, _entry11, _entry_state5, _entry12, _entry13;
    if (!((_entry = entry) == null ? void 0 : (_entry_config = _entry.config) == null ? void 0 : _entry_config.name)) {
        return null;
    }
    var resultEntry = Object.assign(entry, {
        config: {
            name: (_entry1 = entry) == null ? void 0 : (_entry_config1 = _entry1.config) == null ? void 0 : _entry_config1.name,
            path: ((_entry2 = entry) == null ? void 0 : (_entry_config2 = _entry2.config) == null ? void 0 : _entry_config2.path) || null,
            pathRegex: compileRegex((_entry3 = entry) == null ? void 0 : (_entry_config3 = _entry3.config) == null ? void 0 : _entry_config3.pathRegex),
            parameters: ((_entry4 = entry) == null ? void 0 : (_entry_config4 = _entry4.config) == null ? void 0 : _entry_config4.parameters) || {},
            profileParameters: ((_entry5 = entry) == null ? void 0 : (_entry_config5 = _entry5.config) == null ? void 0 : _entry_config5.profileParameters) || {},
            // Not a factor in matching but can be used to control when the cache attempts to fulfill a request
            immediate: ((_entry6 = entry) == null ? void 0 : (_entry_config6 = _entry6.config) == null ? void 0 : _entry_config6.immediate) || false
        },
        state: {
            done: false,
            specificity: -1,
            status: ((_entry7 = entry) == null ? void 0 : (_entry_state = _entry7.state) == null ? void 0 : _entry_state.status) || STATUS_NEW,
            createdAt: ((_entry8 = entry) == null ? void 0 : (_entry_state1 = _entry8.state) == null ? void 0 : _entry_state1.createdAt) || new Date(),
            fetching: ((_entry9 = entry) == null ? void 0 : (_entry_state2 = _entry9.state) == null ? void 0 : _entry_state2.fetching) || null,
            fetched: ((_entry10 = entry) == null ? void 0 : (_entry_state3 = _entry10.state) == null ? void 0 : _entry_state3.fetched) || null,
            error: ((_entry11 = entry) == null ? void 0 : (_entry_state4 = _entry11.state) == null ? void 0 : _entry_state4.error) || null,
            subscribers: ((_entry12 = entry) == null ? void 0 : (_entry_state5 = _entry12.state) == null ? void 0 : _entry_state5.subscribers) || []
        },
        payload: _extends({}, (_entry13 = entry) == null ? void 0 : _entry13.payload)
    });
    score(resultEntry);
    return resultEntry;
}
/**
 * @param { { state: { subscribers: [] } } } entry
 * @param { function } callback
 * @returns { function } unsubscribe function
 */ function subscribe(entry, callback) {
    if (entry === null) {
        logger.warn("Attempted to subscribe to null entry");
        return function() {};
    }
    // Establish a PubSub Subscription for a cache entry
    entry.state.subscribers.push(callback);
    // Returns an unsubscribe function
    // Create and return a callback which will remove this subscription from the subscribers
    // The constructed scope retains reference to original callback
    return function() {
        var index = entry.state.subscribers.indexOf(callback);
        if (index !== -1) {
            entry.state.subscribers.splice(index, 1);
        }
    };
}
function notifySubscribers(entry) {
    // Alert all subscribers that a change has happened in the cache entry
    entry.state.subscribers.forEach(function(subscriberCallback) {
        subscriberCallback(entry);
    });
}
// Atomic function to actually apply changes to the cache
function update(entry, payload) {
    // Replace payload with incoming payload
    Object.assign(entry, {
        payload: payload
    });
    notifySubscribers(entry);
}
function reset(entry) {
    entry.state.status = STATUS_NEW; // eslint-disable-line no-param-reassign
    entry.state.fetching = null; // eslint-disable-line no-param-reassign
    entry.state.fetched = null; // eslint-disable-line no-param-reassign
    entry.state.error = null; // eslint-disable-line no-param-reassign
    entry.state.done = false; // eslint-disable-line no-param-reassign
    entry.payload = {}; // eslint-disable-line no-param-reassign
    notifySubscribers(entry);
}
function markRemoved(entry) {
    entry.state.status = STATUS_REMOVED; // eslint-disable-line no-param-reassign
    entry.state.fetching = null; // eslint-disable-line no-param-reassign
    entry.state.fetched = null; // eslint-disable-line no-param-reassign
    entry.state.error = null; // eslint-disable-line no-param-reassign
    entry.state.done = true; // eslint-disable-line no-param-reassign
    entry.payload = {}; // eslint-disable-line no-param-reassign
    notifySubscribers(entry);
}
function markFetching(entry, index) {
    if (index === void 0) index = null;
    entry.state.status = STATUS_FETCHING; // eslint-disable-line no-param-reassign
    entry.state.fetching = index; // eslint-disable-line no-param-reassign
    notifySubscribers(entry);
}
function markFetched(entry, payload) {
    entry.state.status = STATUS_FETCHED; // eslint-disable-line no-param-reassign
    entry.state.fetched = new Date(); // eslint-disable-line no-param-reassign
    entry.state.done = true; // eslint-disable-line no-param-reassign
    entry.payload = payload; // eslint-disable-line no-param-reassign
    notifySubscribers(entry);
}
function markErrored(entry, error) {
    entry.state.status = STATUS_ERRORED; // eslint-disable-line no-param-reassign
    entry.state.error = error.message; // eslint-disable-line no-param-reassign
    entry.state.done = true; // eslint-disable-line no-param-reassign
    notifySubscribers(entry);
}
function canFulfill(param) {
    var config = param.config, state = param.state;
    var _config, _state;
    return ((_config = config) == null ? void 0 : _config.name) && ((_state = state) == null ? void 0 : _state.status) === STATUS_NEW;
}
function compareName(incoming, existing) {
    // Ensure both input and stored caches have a name property of type string and equal
    return incoming && existing && incoming.name && existing.name && getType(existing.name) === "string" && getType(incoming.name) === "string" && existing.name === incoming.name;
}
function comparePath(incoming, existing) {
    if (!(incoming && existing)) {
        return false;
    }
    var existingPathRegex = compileRegex(existing.pathRegex);
    var incomingPathRegex = compileRegex(incoming.pathRegex);
    if (existing.path) {
        if (incoming.path) {
            // if an existing entry does not have a pathRegex, it must match the incoming entry's path to be considered a match
            return typeof existing.path === "string" && typeof incoming.path === "string" && existing.path === incoming.path;
        }
        if (incomingPathRegex) {
            return incomingPathRegex.test(existing.path);
        }
        return false;
    }
    if (existingPathRegex) {
        if (incoming.path) {
            return existingPathRegex.test(incoming.path);
        }
        if (incomingPathRegex) {
            return existingPathRegex.toString() === incomingPathRegex.toString();
        }
        return false;
    }
    return true;
}
/**
 * Compare two cache entries and return if they match
 * @param {object} param0
 * @param {object} param1
 * @returns {boolean}
 */ function compare(param, param1) {
    var incoming = param.config;
    var existing = param1.config;
    // will return -1 or 1
    var valid = compareName(incoming, existing);
    if (valid) {
        valid = comparePath(incoming, existing);
    }
    if (valid && existing.parameters && Object.keys(existing.parameters).length > 0) {
        valid = isEqual(incoming.parameters, existing.parameters);
    }
    if (valid && existing.profileParameters && Object.keys(existing.profileParameters).length > 0) {
        valid = isEqual(incoming.profileParameters, existing.profileParameters);
    }
    return valid;
}
export { create, subscribe, update, notifySubscribers, reset, markRemoved, markFetching, markFetched, markErrored, canFulfill, compare, score };

//# sourceMappingURL=entry.js.map